
// src/components/admin/NotificationsManagement.js
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import "suneditor/dist/css/suneditor.min.css";
import { 
  Trash as TrashIcon, 
  Ban as BanIcon, 
  CheckCircle as CheckCircleIcon, 
  PlusCircle as PlusCircleIcon, 
  Tag as TagIcon,
  Eye as EyeIcon,
  User as UserIcon,
  Mail as MailIcon,
  Phone as PhoneIcon,
  Calendar as CalendarIcon,
  ShoppingBag as ShoppingBagIcon,
  CreditCard as CreditCardIcon,
  Send as SendIcon,
  Bell as BellIcon,
  Inbox as InboxIcon
} from "lucide-react";
import ContentEditable from 'react-contenteditable';
import { useAuth } from '../../context/AuthContext';

// Default HTML layout for the email content
const defaultEmailLayout = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <title>Email Layout</title>
</head>
<body style="margin:0; padding:0; font-family:Arial, sans-serif; background-color:#f9f9f9;">
  <div style="max-width:600px; margin:auto; border-radius:6px; overflow:hidden; border:1px solid #ddd; background-color:#fff;">
    <div style="background-color:#1a56db; padding:25px 20px; text-align:center;">
      <h1 style="color:#fff; margin:0; font-size:24px;">Bag&Box Notification</h1>
    </div>
    <div style="padding:30px 20px;">
      <h2 style="color:#333; margin-top:0;">Hello {{firstName}},</h2>
      <p>Thank you for being a valued customer. We have some important information to share with you.</p>
      <div style="text-align:center; margin:30px 0;">
        <a href="#" style="padding:12px 30px; background-color:#1a56db; color:#fff; text-decoration:none; border-radius:4px; font-weight:bold;">Visit our website</a>
      </div>
      <p>If you have any questions, please don't hesitate to contact our support team.</p>
    </div>
    <div style="background-color:#1a56db; text-align:center; padding:15px;">
      <p style="margin:0; color:#fff; font-size:14px;">© ${new Date().getFullYear()} Bag&Box. All rights reserved.</p>
    </div>
  </div>
</body>
</html>`;


const NotificationsManagement = () => {
  // --- Sending Notification States ---
  const [users, setUsers] = useState([]);
  const [viewAllNotifications, setViewAllNotifications] = useState(false);
  const { user, userRole, hasPermission } = useAuth();
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // const [filter, setFilter] = useState("all"); // "all" or "custom"
  const [channels, setChannels] = useState({
    email: false,
    sms: false,
    inApp: false,
  });
  const [messageContent, setMessageContent] = useState({
    email: defaultEmailLayout,
    sms: "",
    inApp: "",
  });

  // --- User Selection and Pagination States ---
  const [searchQuery, setSearchQuery] = useState("");
  const [userTypeFilter, setUserTypeFilter] = useState("all");
  const [sortBy, setSortBy] = useState("recent");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const handleVariableInsert = (variableValue) => {
    setMessageContent((prev) => ({
      ...prev,
      email: prev.email + variableValue
    }));
  };

  const [filter, setFilter] = useState("subscribers"); // Changed default to "subscribers"
  const [subscriptionStats, setSubscriptionStats] = useState({ active: 0, total: 0 });

  
  const [status, setStatus] = useState("");
  const [emailSubject, setEmailSubject] = useState("Bag&Box Newsletter");
  const contentEditableRef = useRef(null);

  // --- Admin-Created Notifications List with Pagination ---
  const [adminNotifications, setAdminNotifications] = useState([]);
  const [listStatus, setListStatus] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  
  // --- Email Template States ---
  const [selectedTemplate, setSelectedTemplate] = useState("default");
  const [showVariableMenu, setShowVariableMenu] = useState(false);

  // Available email templates
  const emailTemplates = {
    default: {
      name: "Default Template",
      html: defaultEmailLayout,
    },
    promotional: {
      name: "Promotional",
      html: `<!DOCTYPE html>
              <html>
              <head>
                <meta charset="UTF-8" />
                <title>Special Offer</title>
              </head>
              <body style="margin:0; padding:0; font-family:Arial, sans-serif; background-color:#f7f7f7;">
                <div style="max-width:600px; margin:auto; border-radius:8px; overflow:hidden; border:1px solid #ddd; background-color:#fff;">
                  <div style="background-color:#ff6b6b; padding:30px 20px; text-align:center;">
                    <h1 style="color:#fff; margin:0; font-size:28px;">Special Offer</h1>
                  </div>
                  <div style="padding:30px 20px;">
                    <h2 style="color:#333;">Limited Time Offer!</h2>
                    <p>Hello {{firstName}},</p>
                    <p>We're excited to share our latest promotion with you. Check out these amazing deals!</p>
                    <div style="text-align:center; margin:30px 0;">
                      <a href="#" style="padding:15px 25px; background-color:#ff6b6b; color:#fff; text-decoration:none; border-radius:4px; font-weight:bold;">Shop Now</a>
                    </div>
                    <p>This offer is valid until {expiryDate}. Don't miss out!</p>
                  </div>
                  <div style="background-color:#f1f1f1; text-align:center; padding:15px;">
                    <p style="margin:0; color:#666;">© 2025 Bag&Box. All rights reserved.</p>
                  </div>
                </div>
              </body>
              </html>`,
    },
    newsletter: {
      name: "Newsletter",
      html: `<!DOCTYPE html>
              <html>
              <head>
                <meta charset="UTF-8" />
                <title>Monthly Newsletter</title>
              </head>
              <body style="margin:0; padding:0; font-family:Arial, sans-serif; background-color:#f9f9f9;">
                <div style="max-width:600px; margin:auto; border-radius:8px; overflow:hidden; border:1px solid #ddd; background-color:#fff;">
                  <div style="background-color:#4a6ee0; padding:30px 20px; text-align:center;">
                    <h1 style="color:#fff; margin:0; font-size:28px;">Monthly Newsletter</h1>
                  </div>
                  <div style="padding:30px 20px;">
                    <h2 style="color:#333;">Hello {{firstName}}!</h2>
                    <p>Welcome to our monthly newsletter where we share updates, tips, and exciting news!</p>
                    <div style="background-color:#f7f7f7; padding:15px; border-left:4px solid #4a6ee0; margin:20px 0;">
                      <h3>What's New</h3>
                      <p>Check out our latest products and offers...</p>
                    </div>
                    <div style="text-align:center; margin:25px 0;">
                      <a href="#" style="padding:12px 20px; background-color:#4a6ee0; color:#fff; text-decoration:none; border-radius:4px; font-weight:bold;">Read More</a>
                    </div>
                  </div>
                  <div style="background-color:#f1f1f1; text-align:center; padding:15px;">
                    <p style="margin:0; color:#666;">© 2025 Bag&Box. All rights reserved.</p>
                    <p style="margin:5px 0 0; font-size:12px; color:#999;">You received this email because you subscribed to our newsletter.</p>
                  </div>
                </div>
              </body>
              </html>`,
    },
    welcome: {
      name: "Welcome",
      html: `<!DOCTYPE html>
              <html>
              <head>
                <meta charset="UTF-8" />
                <title>Welcome to Bag&Box</title>
              </head>
              <body style="margin:0; padding:0; font-family:Arial, sans-serif; background-color:#f9f9f9;">
                <div style="max-width:600px; margin:auto; border-radius:8px; overflow:hidden; border:1px solid #ddd; background-color:#fff;">
                  <div style="background-color:#5cb85c; padding:30px 20px; text-align:center;">
                    <h1 style="color:#fff; margin:0; font-size:28px;">Welcome to Bag&Box!</h1>
                  </div>
                  <div style="padding:30px 20px;">
                    <h2 style="color:#333;">Hello {{firstName}}!</h2>
                    <p>Thank you for joining our community. We're thrilled to have you on board!</p>
                    <p>With your new account, you can:<br/><br/></p>
                    <ul style="padding-left:20px; line-height:1.8;">
                      <li>Browse our extensive product catalog</li>
                      <li>Save your favorite items</li>
                      <li>Track your orders easily</li>
                      <li>Access exclusive promotions</li>
                    </ul>
                    <div style="text-align:center; margin:25px 0;">
                      <a href="#" style="padding:12px 20px; background-color:#5cb85c; color:#fff; text-decoration:none; border-radius:4px; font-weight:bold;">Explore Now</a>
                    </div>
                  </div>
                  <div style="background-color:#f1f1f1; text-align:center; padding:15px;">
                    <p style="margin:0; color:#666;">© 2025 Bag&Box. All rights reserved.</p>
                  </div>
                </div>
              </body>
              </html>`,
    },
  };

  // Available variables for insertion
  const variablesList = [
    { label: "First Name", value: "{{firstName}}", icon: <UserIcon size={16} /> },
    { label: "Last Name", value: "{{lastName}}", icon: <UserIcon size={16} /> },
    { label: "Full Name", value: "{{fullName}}", icon: <UserIcon size={16} /> },
    { label: "Email", value: "{{email}}", icon: <MailIcon size={16} /> },
    { label: "Phone", value: "{{phone}}", icon: <PhoneIcon size={16} /> },
    { label: "Company", value: "{{company}}", icon: <TagIcon size={16} /> },
    { label: "Date", value: "{{date}}", icon: <CalendarIcon size={16} /> },
  ];

  const fetchSubscriptionStats = async () => {
    const token = localStorage.getItem("token");
    try {
      const res = await axios.get("/api/admin/subscriptions/stats", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubscriptionStats(res.data);
    } catch (error) {
      console.error("Error fetching subscription stats:", error);
    }
  };

  // If Email channel is selected and no content yet, load the default HTML layout
  useEffect(() => {
    if (channels.email && !messageContent.email) {
      setMessageContent((prev) => ({ ...prev, email: defaultEmailLayout }));
    }
  }, [channels.email, messageContent.email]);

  // Fetch users for custom selection with pagination, search, and filtering
  useEffect(() => {
    if (filter === "custom") {
      fetchUsers();
    }
  }, [filter, currentPage, userTypeFilter, sortBy]);

  // Function to fetch users with pagination, search, and filters
  const fetchUsers = async () => {
    setIsLoading(true);
    const token = localStorage.getItem("token");
    
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: 15, // Adjust limit as needed
        sortBy: getSortField(),
        sortOrder: getSortOrder(),
      });
      
      // Add search param if present
      if (searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }
      
      // Add type filter if not "all"
      if (userTypeFilter !== "all") {
        params.append('filter', userTypeFilter);
      }
      
      const res = await axios.get(`/api/admin/users?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      console.log("Fetched users:", res.data);
      
      // Handle paginated response
      if (res.data.users) {
        setUsers(res.data.users);
        setTotalPages(res.data.totalPages || 1);
        setTotalUsers(res.data.total || 0);
      } else {
        setUsers(res.data || []);
        setTotalPages(1);
        setTotalUsers(res.data?.length || 0);
      }
      
      setIsLoading(false);
    } catch (err) {
      console.error("Error fetching users:", err);
      setStatus("Error fetching user list.");
      setIsLoading(false);
    }
  };

  // Function to handle page changes
  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Function to handle search
  const handleSearch = () => {
    setCurrentPage(1); // Reset to first page on new search
    fetchUsers();
  };

  // Helper functions for sorting
  const getSortField = () => {
    switch (sortBy) {
      case "name":
        return "firstName";
      case "email":
        return "email";
      case "recent":
      default:
        return "createdAt";
    }
  };

  const getSortOrder = () => {
    // For "recent" we want newest first (desc), for others asc order
    return sortBy === "recent" ? "desc" : "asc";
  };

  // Add this function to check for system role permissions
  const hasSystemRolePermission = (userObj, permissionsList) => {
    if (!userObj || !permissionsList) return false;
    
    // Check if the user has system:manage permission
    if (permissionsList.includes('system:manage')) return true;
    
    // Or has all notification permissions
    if (permissionsList.includes('notification:manage')) return true;
    
    // Check for specific notification permissions that would grant full access
    const hasAllNotificationPermissions = 
      permissionsList.includes('notification:create') && 
      permissionsList.includes('notification:read') &&
      permissionsList.includes('notification:update') &&
      permissionsList.includes('notification:delete');
      
    return hasAllNotificationPermissions;
  };

  // Fetch admin-created notifications for management with pagination
  const fetchAdminNotifications = async (currentPage = page) => {
    const token = localStorage.getItem("token");
    try {
      const endpoint = viewAllNotifications 
        ? `/api/notifications/admin/created?page=${currentPage}&limit=20` 
        : `/api/notifications/admin/specific-created?page=${currentPage}&limit=20`;
      
      const res = await axios.get(
        endpoint,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      console.log("API Response:", res.data);
      
      // Don't modify the original data to avoid unexpected re-renders
      setAdminNotifications(res.data.notifications);
      setTotalPages(res.data.totalPages);
      setPage(res.data.currentPage);
      setListStatus("");
    } catch (error) {
      console.error("Error fetching admin notifications:", error);
      setListStatus("Error fetching notifications: " + error.message);
    }
  };

  //  helper function in your component
  const formatUserDisplay = (user) => {
    if (!user) return "Unknown";
    
    if (typeof user === 'object' && user !== null) {
      if (user.firstName) {
        return `${user.firstName} ${user.lastName || ""} (${user.email || ""})`;
      }
      
    }
    
    // If it's just an ID (string), show a better message
    return "User details unavailable";
  };
  

  //useEffect to react to changes in the viewAllNotifications state
  useEffect(() => {
    fetchAdminNotifications(1);
    fetchSubscriptionStats();
  }, [viewAllNotifications]);

  const handleUserSelection = (userId, isSelected) => {
    if (isSelected) {
      setSelectedUsers((prev) => [...prev, userId]);
    } else {
      setSelectedUsers((prev) => prev.filter((id) => id !== userId));
    }
  };

  const handleSend = async () => {
    setStatus("Sending...");
    const token = localStorage.getItem("token");
    try {
      console.log("Sending notification with payload:", {
        selectedUsers: filter === "custom" ? selectedUsers : [],
        filter,
        channels,
        messageContent,
        emailSubject,
      });
      await axios.post(
        "/api/notifications/admin/send",
        {
          selectedUsers: filter === "custom" ? selectedUsers : [],
          filter,
          channels,
          messageContent,
          emailSubject,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setStatus("Notifications sent successfully!");
      // Refresh the admin notifications list after sending
      fetchAdminNotifications();
    } catch (error) {
      console.error("Error sending notifications:", error);
      setStatus(
        "Error sending notifications: " +
          (error.response?.data?.error || error.message)
      );
    }
  };

  // Toggle (enable/disable) a notification's active status
  const toggleNotification = async (notificationId, currentStatus) => {
    const token = localStorage.getItem("token");
    try {
      console.log(
        `Toggling notification ${notificationId} from active=${currentStatus} to active=${!currentStatus}`
      );
      await axios.put(
        `/api/notifications/admin/${notificationId}`,
        { active: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Update local state
      setAdminNotifications((prev) =>
        prev.map((notif) =>
          notif._id === notificationId ? { ...notif, active: !currentStatus } : notif
        )
      );
    } catch (error) {
      console.error("Error updating notification status:", error);
      setListStatus("Error updating notification status: " + error.message);
    }
  };

  // Remove a notification
  const removeNotification = async (notificationId) => {
    const token = localStorage.getItem("token");
    try {
      console.log(`Removing notification ${notificationId}`);
      await axios.delete(`/api/notifications/admin/${notificationId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Update local state: remove the deleted notification
      setAdminNotifications((prev) =>
        prev.filter((notif) => notif._id !== notificationId)
      );
    } catch (error) {
      console.error("Error deleting notification:", error);
      setListStatus("Error deleting notification: " + error.message);
    }
  };

  // Pagination controls
  const handlePrevPage = () => {
    if (page > 1) {
      fetchAdminNotifications(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages) {
      fetchAdminNotifications(page + 1);
    }
  };

  // Function to handle template selection
  const handleTemplateChange = (e) => {
    const templateKey = e.target.value;
    setSelectedTemplate(templateKey);
    setMessageContent((prev) => ({
      ...prev,
      email: emailTemplates[templateKey].html,
    }));
  };

  // Function to insert a variable at the cursor position in the editor
  const insertVariable = (variable) => {
    if (contentEditableRef.current) {
      // Get the current selection in the editor
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        // Create a text node with the variable placeholder
        const textNode = document.createTextNode(variable);
        // Insert the text node at the current cursor position
        range.insertNode(textNode);
        // Move the cursor after the inserted text node
        range.setStartAfter(textNode);
        range.setEndAfter(textNode);
        selection.removeAllRanges();
        selection.addRange(range);
      } else {
        // If no selection, append the variable at the end
        contentEditableRef.current.innerHTML += ' ' + variable;
      }
      // Update the state with the new HTML content of the editor
      setMessageContent((prev) => ({
        ...prev,
        email: contentEditableRef.current.innerHTML,
      }));
      setShowVariableMenu(false);
    }
  };
  

  // Handle change in the contentEditable component
  const handleContentEditableChange = (evt) => {
    setMessageContent({
      ...messageContent,
      email: evt.target.value
    });
  };

  if (loading) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center">
        <img src='../images/loading.gif'/>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-8">Notification/Newsletter Management</h2>
      <div className="px-4 py-4 bg-white shadow-lg rounded-2xl">

        {/*Create New Notification */}
        <div>
          <div className="mb-6 pt-2">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3 rounded-lg shadow-md mb-8">
              <h3 className="text-md font-semibold text-white">Create New Notification</h3>
            </div>

            <div className="p-4"> 
              {/* User Selection */}
              <h4 className="text-md font-semibold text-gray-700 mb-4">
                Select Recipients
              </h4>
              <div className="flex flex-col sm:flex-row sm:space-x-6">
                {/* <label className="inline-flex items-center mb-2 sm:mb-0">
                  <input
                    type="radio"
                    value="all"
                    checked={filter === "all"}
                    onChange={() => setFilter("all")}
                    className="form-radio text-blue-600"
                  />
                  <span className="ml-2">All Users</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    value="custom"
                    checked={filter === "custom"}
                    onChange={() => setFilter("custom")}
                    className="form-radio text-blue-600"
                  />
                  <span className="ml-2">Custom Selection</span>
                </label> */}
                 <label className="inline-flex items-center mb-2 sm:mb-0">
                  <input
                    type="radio"
                    value="subscribers"
                    checked={filter === "subscribers"}
                    onChange={() => setFilter("subscribers")}
                    className="form-radio text-blue-600"
                  />
                  <span className="ml-2">All Subscribers ({subscriptionStats.active})</span>
                </label>
                <label className="inline-flex items-center mb-2 sm:mb-0">
                  <input
                    type="radio"
                    value="all"
                    checked={filter === "all"}
                    onChange={() => setFilter("all")}
                    className="form-radio text-blue-600"
                  />
                  <span className="ml-2">All Users</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    value="custom"
                    checked={filter === "custom"}
                    onChange={() => setFilter("custom")}
                    className="form-radio text-blue-600"
                  />
                  <span className="ml-2">Custom Selection</span>
                </label>
              </div>
           
              {filter === "custom" && (
                <div className="mb-6 mt-4">
                  <h4 className="text-md font-semibold text-gray-700 mb-2">
                    Select Individual Users
                  </h4>
                  
                  {/* Search and Filter Controls */}
                  <div className="mb-4 flex flex-col md:flex-row gap-3">
                    <div className="relative flex-grow">
                      <input
                        type="text"
                        placeholder="Search by name or email..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      />
                      <button 
                        className="absolute right-2 top-2 text-gray-400 hover:text-blue-500"
                        onClick={handleSearch}
                        style={{transform:'none', boxShadow:'none', border: 'none'}}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </button>
                    </div>
                    
                    {/* <div className="flex-shrink-0">
                      <select
                        className="w-full md:w-auto px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={userTypeFilter}
                        onChange={(e) => setUserTypeFilter(e.target.value)}
                      >
                        <option value="all">All Users</option>
                        <option value="subscribers">Subscribers Only</option>
                        <option value="active">Recently Active</option>
                      </select>
                    </div> */}
                    
                    <div className="flex-shrink-0">
                      <select
                        className="w-full md:w-auto pr-8 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                      >
                        <option value="recent">Recently Added</option>
                        <option value="name">Name (A-Z)</option>
                        <option value="email">Email (A-Z)</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Users Grid with Loading State */}
                  {isLoading ? (
                    <div className="flex justify-center items-center h-40">
                      <div className="spinner-border text-blue-500" role="status">
                        <span className="sr-only">Loading...</span>
                        <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      </div>
                    </div>
                  ) : users.length > 0 ? (
                    <div className="border p-4 rounded bg-gray-50">
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 max-h-60 overflow-y-auto">
                        {users.map((user) => (
                          <label key={user._id} className="flex items-center space-x-2 px-3 py-2 rounded hover:bg-gray-100 transition bg-white shadow-sm">
                            <input
                              type="checkbox"
                              value={user._id}
                              onChange={(e) =>
                                handleUserSelection(user._id, e.target.checked)
                              }
                              checked={selectedUsers.includes(user._id)}
                              className="form-checkbox text-blue-600 rounded"
                            />
                            <div className="text-sm">
                              <div className="font-medium text-gray-700">{user.email}</div>
                              {user.firstName && (
                                <div className="text-gray-500 text-xs">{user.firstName} {user.lastName}</div>
                              )}
                            </div>
                          </label>
                        ))}
                      </div>
                      
                      {/* Pagination Controls */}
                      <div className="mt-4 flex flex-col sm:flex-row justify-between items-center">
                        <div className="text-sm text-gray-500 mb-2 sm:mb-0">
                          Showing {users.length} of {totalUsers} users
                        </div>
                        <div className="flex justify-center space-x-2">
                          <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className={`px-3 py-1 rounded ${
                              currentPage === 1
                                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                                : "bg-blue-100 text-blue-700 hover:bg-blue-200"
                            }`}
                          >
                            &laquo; Prev
                          </button>
                          <div className="flex items-center px-2">
                            <span className="text-sm">Page {currentPage} of {totalPages}</span>
                          </div>
                          <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages || totalPages === 0}
                            className={`px-3 py-1 rounded ${
                              currentPage === totalPages || totalPages === 0
                                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                                : "bg-blue-100 text-blue-700 hover:bg-blue-200"
                            }`}
                          >
                            Next &raquo;
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-10 border rounded bg-gray-50">
                      <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Try adjusting your search or filter criteria.
                      </p>
                    </div>
                  )}
                  
                  {/* Selection Summary */}
                  <div className="mt-2 flex justify-between text-sm text-gray-500">
                    <span>{selectedUsers.length} users selected</span>
                    {selectedUsers.length > 0 && (
                      <button 
                        onClick={() => setSelectedUsers([])}
                        className="text-red-500 hover:text-red-700"
                      >
                        Clear selection
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Channel Selection */}
              <div className="mb-6 mt-8">
                <h4 className="text-md font-semibold text-gray-700 mb-4">
                  Select Notification Channels
                </h4>
                <div className="flex flex-col sm:flex-row sm:space-x-6 mt-2">
                  <label className="inline-flex items-center mb-2 sm:mb-0 bg-blue-50 px-4 py-2 rounded-lg hover:bg-blue-100 transition">
                    <input
                      type="checkbox"
                      checked={channels.email}
                      onChange={(e) =>
                        setChannels({ ...channels, email: e.target.checked })
                      }
                      className="form-checkbox text-blue-600 rounded"
                    />
                    <span className="ml-2 flex items-center">
                      <MailIcon size={16} className="mr-1" /> Email
                    </span>
                  </label>
                  <label className="inline-flex items-center mb-2 sm:mb-0 bg-green-50 px-4 py-2 rounded-lg hover:bg-green-100 transition">
                    <input
                      type="checkbox"
                      checked={channels.sms}
                      onChange={(e) =>
                        setChannels({ ...channels, sms: e.target.checked })
                      }
                      className="form-checkbox text-green-600 rounded"
                    />
                    <span className="ml-2 flex items-center">
                      <PhoneIcon size={16} className="mr-1" /> SMS
                    </span>
                  </label>
                  <label className="inline-flex items-center bg-purple-50 px-4 py-2 rounded-lg hover:bg-purple-100 transition">
                    <input
                      type="checkbox"
                      checked={channels.inApp}
                      onChange={(e) =>
                        setChannels({ ...channels, inApp: e.target.checked })
                      }
                      className="form-checkbox text-purple-600 rounded"
                    />
                    <span className="ml-2 flex items-center">
                      <BellIcon size={16} className="mr-1" /> In-App Notification
                    </span>
                  </label>
                </div>
              </div>

              {/* Message Composer */}
              <div className="mb-6">
                {channels.email && (
                  <div className="mb-4">
                    <h3 className="text-xl font-semibold text-gray-700 mb-2 flex items-center">
                      <MailIcon size={20} className="mr-2 text-blue-600" />
                      Email Content
                    </h3>
                    <div className="mb-4">
                      <label className="block text-gray-700 mb-2">Email Subject</label>
                      <input
                        type="text"
                        value={emailSubject}
                        onChange={(e) => setEmailSubject(e.target.value)}
                        placeholder="Enter email subject"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div className="mb-4">
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-gray-700">Choose Template</label>
                      </div>
                      <select
                        value={selectedTemplate}
                        onChange={handleTemplateChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
                      >
                        {Object.keys(emailTemplates).map((key) => (
                          <option key={key} value={key}>
                            {emailTemplates[key].name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Variable insertion tools */}
                    <div className="mb-4">
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-gray-700">Insert Variables</label>
                        <button
                          type="button"
                          onClick={() => setShowVariableMenu(!showVariableMenu)}
                          className="flex items-center text-blue-600 hover:text-blue-800"
                        >
                          <TagIcon size={16} className="mr-1" /> 
                          {showVariableMenu ? "Hide Variables" : "Show Variables"}
                        </button>
                      </div>
                      
                      {showVariableMenu && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 p-3 bg-gray-50 rounded-md mb-4">
                          {variablesList.map((variable) => (
                            <button
                              key={variable.value}
                              onClick={() => insertVariable(variable.value)}
                              className="flex items-center px-3 py-2 bg-white rounded border hover:bg-blue-50 transition text-sm"
                            >
                              {variable.icon}
                              <span className="ml-2">{variable.label}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Editable Email Preview */}
                    <div className="border rounded-md bg-white">
                      <div className="mb-2 p-2 bg-gray-100 rounded text-sm text-gray-700 mx-4 mt-4">
                        <strong>Subject:</strong> {emailSubject}
                      </div>
                      <div className="p-4 email-preview">
                        <div className="border p-4 rounded-md shadow-sm mb-2 bg-gray-50">
                          <p className="text-sm text-gray-600 mb-2">
                            <strong>Note:</strong> Click anywhere in the template below to edit. You can add variables using the buttons above.
                          </p>
                        </div>
                        <ContentEditable
                          innerRef={contentEditableRef}
                          html={messageContent.email}
                          disabled={false}
                          onChange={handleContentEditableChange}
                          className="min-h-96 outline-none border border-gray-200 p-4 rounded-md focus:border-blue-400 focus:ring-2 focus:ring-blue-200"
                        />
                      </div>
                    </div>
                    
                    <div className="mt-2 text-sm text-gray-600">
                      <p>Use variables like <code>{"{{firstName}}"}</code> to personalize your email. Click "Show Variables" to see all options.</p>
                    </div>
                  </div>
                )}

                {channels.sms && (
                  <div className="mb-4">
                    <h3 className="text-xl font-semibold text-gray-700 mb-2 flex items-center">
                      <PhoneIcon size={20} className="mr-2 text-green-600" />
                      SMS Content
                    </h3>
                    <div className="mb-2">
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-gray-700">Message Text (160 characters recommended)</label>
                        <span className="text-sm text-gray-500">{messageContent.sms.length} characters</span>
                      </div>
                      <textarea
                        rows="3"
                        className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-green-400"
                        placeholder="Compose your SMS message here..."
                        value={messageContent.sms}
                        onChange={(e) =>
                          setMessageContent({ ...messageContent, sms: e.target.value })
                        }
                      />
                    </div>
                    <div className="mt-2 text-sm text-gray-600">
                      <p>For SMS, you can use variables like <code>{"{firstName}"}</code> too. Keep messages concise for better delivery rates.</p>
                    </div>
                  </div>
                )}
                {channels.inApp && (
                  <div className="mb-4">
                    <h3 className="text-xl font-semibold text-gray-700 mb-2 flex items-center">
                      <BellIcon size={20} className="mr-2 text-purple-600" />
                      In-App Notification Content
                    </h3>
                    <textarea
                      rows="3"
                      className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-purple-400"
                      placeholder="Compose your in-app notification here..."
                      value={messageContent.inApp}
                      onChange={(e) =>
                        setMessageContent({ ...messageContent, inApp: e.target.value })
                      }
                    />
                    <div className="mt-2 text-sm text-gray-600">
                      <p>In-app notifications should be short and actionable. You can include variables like <code>{"{firstName}"}</code> here too.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Send Button & Status */}
            <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-4 mb-10 px-4">
              <button
                onClick={handleSend}
                disabled={!channels.email && !channels.sms && !channels.inApp}
                className={`px-6 py-3 rounded-lg transition-colors flex items-center ${
                  (!channels.email && !channels.sms && !channels.inApp)
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-green-600 text-white hover:bg-green-700"
                }`}
              >
                <SendIcon size={18} className="mr-2" />
                Send Notification
              </button>
              {status && (
                <div className={`text-lg font-medium ${status.includes('Error') ? 'text-red-600' : 'text-green-600'}`}>
                  {status}
                </div>
              )}
            </div>

          </div>
        </div>

        {/* --- Section 2: Manage Created Notifications --- */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3 rounded-lg shadow-md mb-8">
          <div className="flex justify-between items-center">
            <h3 className="text-md font-semibold text-white">Manage Created Notifications</h3>
            {/* Show toggle button for super admins or admins with proper permissions */}
            {(user?.isSuperAdmin || 
                  hasPermission('notification', 'manage') || 
                  hasPermission('system', 'manage')) && (
                  <button
                    onClick={() => setViewAllNotifications(!viewAllNotifications)}
                    className={`px-3 py-1 rounded-md text-xs font-medium ${
                      viewAllNotifications 
                        ? "bg-white text-indigo-600 hover:bg-gray-100" 
                        : "bg-indigo-400 text-white hover:bg-indigo-500"
                    }`}
                  >
                    {viewAllNotifications ? "Show My Notifications" : "Show All Notifications"}
                  </button>
                )}
          </div>
        </div>

        {listStatus && <div className="mb-4 text-red-600">{listStatus}</div>}
        {adminNotifications.length === 0 ? (
          <div className="text-center py-12">
            <div className="mb-4">
              <InboxIcon size={64} className="text-gray-300 mx-auto" />
            </div>
            <p className="text-gray-500 text-lg">No notifications have been created yet.</p>
            <p className="text-gray-400 mt-2">Send your first notification using the form above.</p>
          </div>
        ) : (
          <div className="overflow-x-auto -mx-2 lg:mx-0">
            <table className="min-w-full bg-white shadow-md rounded-lg">
              <thead className="bg-gray-50">
                <tr>
                  <th className="py-3 px-3 lg:px-4 border-b text-sm font-medium text-gray-700">Message</th>
                  <th className="py-3 px-3 lg:px-4 border-b text-sm font-medium text-gray-700 hidden lg:table-cell">Type</th>
                  <th className="py-3 px-3 lg:px-4 border-b text-sm font-medium text-gray-700 hidden lg:table-cell">Created At</th>
                  {viewAllNotifications && (
                    <th className="py-3 px-3 lg:px-4 border-b text-sm font-medium text-gray-700 hidden lg:table-cell">Created For</th>
                  )}
                  <th className="py-3 px-3 lg:px-4 border-b text-sm font-medium text-gray-700 hidden lg:table-cell">Status</th>
                  <th className="py-3 px-3 lg:px-4 border-b text-sm font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {adminNotifications.map((notif) => (
                  <tr key={notif._id} className="text-center hover:bg-gray-50 border-b">
                    <td className="py-3 px-3 lg:px-4 text-sm">
                      {notif.message}                     
                    </td>
                    <td className="py-3 px-3 lg:px-4 text-sm hidden lg:table-cell">{notif.type}</td>
                    <td className="py-3 px-3 lg:px-4 text-sm hidden lg:table-cell">
                      {new Date(notif.createdAt).toLocaleString()}
                    </td>
                    {viewAllNotifications === true && (
                      <td className="py-3 px-3 lg:px-4 text-sm hidden lg:table-cell">
                       {formatUserDisplay(notif.user)}
                      </td>
                    )}
                    <td className="py-3 px-3 lg:px-4 text-sm hidden lg:table-cell">
                      <span className={`inline-block px-2 py-1 rounded-full text-sm ${
                        notif.active 
                          ? "bg-green-100 text-green-800" 
                          : "bg-gray-100 text-gray-800"
                      }`}>
                        {notif.active ? "Active" : "Disabled"}
                      </span>
                    </td>
                    <td className="py-3 px-3 lg:px-4">
                      <div className="flex flex-row justify-center space-x-4">
                        <button
                          onClick={() => toggleNotification(notif._id, notif.active)}
                          style={{transform: 'none', boxShadow:'none'}}
                          className={`p-2 rounded-full ${
                            notif.active
                              ? "bg-red-100 text-red-500 hover:bg-red-200"
                              : "bg-green-100 text-green-500 hover:bg-green-200"
                          }`}
                          title={notif.active ? "Disable" : "Enable"}
                        >
                          {notif.active ? <BanIcon size={20} /> : <CheckCircleIcon size={20} />}
                        </button>
                        <button
                          onClick={() => removeNotification(notif._id)}
                          style={{transform: 'none', boxShadow:'none'}}
                          className="p-2 rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200"
                          title="Remove"
                        >
                          <TrashIcon size={20} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {adminNotifications.length > 0 && (
          <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-3">
            <button
              onClick={handlePrevPage}
              disabled={page === 1}
              className="w-full sm:w-auto px-5 py-2.5 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 disabled:opacity-50 flex items-center justify-center text-base"
            >
              <span className="mr-1">←</span> Previous
            </button>
            <span className="text-gray-700 text-base">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={handleNextPage}
              disabled={page === totalPages}
              className="w-full sm:w-auto px-5 py-2.5 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 disabled:opacity-50 flex items-center justify-center text-base"
            >
              Next <span className="ml-1">→</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsManagement;











// // before showing the specific notification and view all
// // src/components/admin/NotificationsManagement.js
// import React, { useState, useEffect, useRef } from "react";
// import axios from "axios";
// import "suneditor/dist/css/suneditor.min.css";
// import { 
//   Trash as TrashIcon, 
//   Ban as BanIcon, 
//   CheckCircle as CheckCircleIcon, 
//   PlusCircle as PlusCircleIcon, 
//   Tag as TagIcon,
//   Eye as EyeIcon,
//   User as UserIcon,
//   Mail as MailIcon,
//   Phone as PhoneIcon,
//   Calendar as CalendarIcon,
//   ShoppingBag as ShoppingBagIcon,
//   CreditCard as CreditCardIcon,
//   Send as SendIcon,
//   Bell as BellIcon,
//   Inbox as InboxIcon
// } from "lucide-react";
// import ContentEditable from 'react-contenteditable';

// // Default HTML layout for the email content
// const defaultEmailLayout = `<!DOCTYPE html>
// <html>
// <head>
//   <meta charset="UTF-8" />
//   <title>Email Layout</title>
// </head>
// <body style="margin:0; padding:0; font-family:Arial, sans-serif; background-color:#f9f9f9;">
//   <div style="max-width:600px; margin:auto; border-radius:6px; overflow:hidden; border:1px solid #ddd; background-color:#fff;">
//     <div style="background-color:#1a56db; padding:25px 20px; text-align:center;">
//       <h1 style="color:#fff; margin:0; font-size:24px;">Bag&Box Notification</h1>
//     </div>
//     <div style="padding:30px 20px;">
//       <h2 style="color:#333; margin-top:0;">Hello {{firstName}},</h2>
//       <p>Thank you for being a valued customer. We have some important information to share with you.</p>
//       <div style="text-align:center; margin:30px 0;">
//         <a href="#" style="padding:12px 30px; background-color:#1a56db; color:#fff; text-decoration:none; border-radius:4px; font-weight:bold;">Visit our website</a>
//       </div>
//       <p>If you have any questions, please don't hesitate to contact our support team.</p>
//     </div>
//     <div style="background-color:#1a56db; text-align:center; padding:15px;">
//       <p style="margin:0; color:#fff; font-size:14px;">© ${new Date().getFullYear()} Bag&Box. All rights reserved.</p>
//     </div>
//   </div>
// </body>
// </html>`;


// const NotificationsManagement = () => {
//   // --- Sending Notification States ---
//   const [users, setUsers] = useState([]);
//   const [selectedUsers, setSelectedUsers] = useState([]);
//   const [loading, setLoading] = useState(false);
//   // const [filter, setFilter] = useState("all"); // "all" or "custom"
//   const [channels, setChannels] = useState({
//     email: false,
//     sms: false,
//     inApp: false,
//   });
//   const [messageContent, setMessageContent] = useState({
//     email: defaultEmailLayout,
//     sms: "",
//     inApp: "",
//   });

//   // --- User Selection and Pagination States ---
//   const [searchQuery, setSearchQuery] = useState("");
//   const [userTypeFilter, setUserTypeFilter] = useState("all");
//   const [sortBy, setSortBy] = useState("recent");
//   const [currentPage, setCurrentPage] = useState(1);
//   const [totalUsers, setTotalUsers] = useState(0);
//   const [isLoading, setIsLoading] = useState(false);

//   const handleVariableInsert = (variableValue) => {
//     setMessageContent((prev) => ({
//       ...prev,
//       email: prev.email + variableValue
//     }));
//   };

//   const [filter, setFilter] = useState("subscribers"); // Changed default to "subscribers"
//   const [subscriptionStats, setSubscriptionStats] = useState({ active: 0, total: 0 });

  
//   const [status, setStatus] = useState("");
//   const [emailSubject, setEmailSubject] = useState("Bag&Box Newsletter");
//   const contentEditableRef = useRef(null);

//   // --- Admin-Created Notifications List with Pagination ---
//   const [adminNotifications, setAdminNotifications] = useState([]);
//   const [listStatus, setListStatus] = useState("");
//   const [page, setPage] = useState(1);
//   const [totalPages, setTotalPages] = useState(0);
  
//   // --- Email Template States ---
//   const [selectedTemplate, setSelectedTemplate] = useState("default");
//   const [showVariableMenu, setShowVariableMenu] = useState(false);

//   // Available email templates
//   const emailTemplates = {
//     default: {
//       name: "Default Template",
//       html: defaultEmailLayout,
//     },
//     promotional: {
//       name: "Promotional",
//       html: `<!DOCTYPE html>
//               <html>
//               <head>
//                 <meta charset="UTF-8" />
//                 <title>Special Offer</title>
//               </head>
//               <body style="margin:0; padding:0; font-family:Arial, sans-serif; background-color:#f7f7f7;">
//                 <div style="max-width:600px; margin:auto; border-radius:8px; overflow:hidden; border:1px solid #ddd; background-color:#fff;">
//                   <div style="background-color:#ff6b6b; padding:30px 20px; text-align:center;">
//                     <h1 style="color:#fff; margin:0; font-size:28px;">Special Offer</h1>
//                   </div>
//                   <div style="padding:30px 20px;">
//                     <h2 style="color:#333;">Limited Time Offer!</h2>
//                     <p>Hello {{firstName}},</p>
//                     <p>We're excited to share our latest promotion with you. Check out these amazing deals!</p>
//                     <div style="text-align:center; margin:30px 0;">
//                       <a href="#" style="padding:15px 25px; background-color:#ff6b6b; color:#fff; text-decoration:none; border-radius:4px; font-weight:bold;">Shop Now</a>
//                     </div>
//                     <p>This offer is valid until {expiryDate}. Don't miss out!</p>
//                   </div>
//                   <div style="background-color:#f1f1f1; text-align:center; padding:15px;">
//                     <p style="margin:0; color:#666;">© 2025 Bag&Box. All rights reserved.</p>
//                   </div>
//                 </div>
//               </body>
//               </html>`,
//     },
//     newsletter: {
//       name: "Newsletter",
//       html: `<!DOCTYPE html>
//               <html>
//               <head>
//                 <meta charset="UTF-8" />
//                 <title>Monthly Newsletter</title>
//               </head>
//               <body style="margin:0; padding:0; font-family:Arial, sans-serif; background-color:#f9f9f9;">
//                 <div style="max-width:600px; margin:auto; border-radius:8px; overflow:hidden; border:1px solid #ddd; background-color:#fff;">
//                   <div style="background-color:#4a6ee0; padding:30px 20px; text-align:center;">
//                     <h1 style="color:#fff; margin:0; font-size:28px;">Monthly Newsletter</h1>
//                   </div>
//                   <div style="padding:30px 20px;">
//                     <h2 style="color:#333;">Hello {{firstName}}!</h2>
//                     <p>Welcome to our monthly newsletter where we share updates, tips, and exciting news!</p>
//                     <div style="background-color:#f7f7f7; padding:15px; border-left:4px solid #4a6ee0; margin:20px 0;">
//                       <h3>What's New</h3>
//                       <p>Check out our latest products and offers...</p>
//                     </div>
//                     <div style="text-align:center; margin:25px 0;">
//                       <a href="#" style="padding:12px 20px; background-color:#4a6ee0; color:#fff; text-decoration:none; border-radius:4px; font-weight:bold;">Read More</a>
//                     </div>
//                   </div>
//                   <div style="background-color:#f1f1f1; text-align:center; padding:15px;">
//                     <p style="margin:0; color:#666;">© 2025 Bag&Box. All rights reserved.</p>
//                     <p style="margin:5px 0 0; font-size:12px; color:#999;">You received this email because you subscribed to our newsletter.</p>
//                   </div>
//                 </div>
//               </body>
//               </html>`,
//     },
//     welcome: {
//       name: "Welcome",
//       html: `<!DOCTYPE html>
//               <html>
//               <head>
//                 <meta charset="UTF-8" />
//                 <title>Welcome to Bag&Box</title>
//               </head>
//               <body style="margin:0; padding:0; font-family:Arial, sans-serif; background-color:#f9f9f9;">
//                 <div style="max-width:600px; margin:auto; border-radius:8px; overflow:hidden; border:1px solid #ddd; background-color:#fff;">
//                   <div style="background-color:#5cb85c; padding:30px 20px; text-align:center;">
//                     <h1 style="color:#fff; margin:0; font-size:28px;">Welcome to Bag&Box!</h1>
//                   </div>
//                   <div style="padding:30px 20px;">
//                     <h2 style="color:#333;">Hello {{firstName}}!</h2>
//                     <p>Thank you for joining our community. We're thrilled to have you on board!</p>
//                     <p>With your new account, you can:<br/><br/></p>
//                     <ul style="padding-left:20px; line-height:1.8;">
//                       <li>Browse our extensive product catalog</li>
//                       <li>Save your favorite items</li>
//                       <li>Track your orders easily</li>
//                       <li>Access exclusive promotions</li>
//                     </ul>
//                     <div style="text-align:center; margin:25px 0;">
//                       <a href="#" style="padding:12px 20px; background-color:#5cb85c; color:#fff; text-decoration:none; border-radius:4px; font-weight:bold;">Explore Now</a>
//                     </div>
//                   </div>
//                   <div style="background-color:#f1f1f1; text-align:center; padding:15px;">
//                     <p style="margin:0; color:#666;">© 2025 Bag&Box. All rights reserved.</p>
//                   </div>
//                 </div>
//               </body>
//               </html>`,
//     },
//   };

//   // Available variables for insertion
//   const variablesList = [
//     { label: "First Name", value: "{{firstName}}", icon: <UserIcon size={16} /> },
//     { label: "Last Name", value: "{{lastName}}", icon: <UserIcon size={16} /> },
//     { label: "Full Name", value: "{{fullName}}", icon: <UserIcon size={16} /> },
//     { label: "Email", value: "{{email}}", icon: <MailIcon size={16} /> },
//     { label: "Phone", value: "{{phone}}", icon: <PhoneIcon size={16} /> },
//     { label: "Company", value: "{{company}}", icon: <TagIcon size={16} /> },
//     { label: "Date", value: "{{date}}", icon: <CalendarIcon size={16} /> },
//   ];

//   const fetchSubscriptionStats = async () => {
//     const token = localStorage.getItem("token");
//     try {
//       const res = await axios.get("/api/admin/subscriptions/stats", {
//         headers: { Authorization: `Bearer ${token}` },
//       });
//       setSubscriptionStats(res.data);
//     } catch (error) {
//       console.error("Error fetching subscription stats:", error);
//     }
//   };

//   // If Email channel is selected and no content yet, load the default HTML layout
//   useEffect(() => {
//     if (channels.email && !messageContent.email) {
//       setMessageContent((prev) => ({ ...prev, email: defaultEmailLayout }));
//     }
//   }, [channels.email, messageContent.email]);

//   // Fetch users for custom selection
//   // Fetch users for custom selection with pagination, search, and filtering
//   useEffect(() => {
//     if (filter === "custom") {
//       fetchUsers();
//     }
//   }, [filter, currentPage, userTypeFilter, sortBy]);

//   // Function to fetch users with pagination, search, and filters
//   const fetchUsers = async () => {
//     setIsLoading(true);
//     const token = localStorage.getItem("token");
    
//     try {
//       const params = new URLSearchParams({
//         page: currentPage,
//         limit: 15, // Adjust limit as needed
//         sortBy: getSortField(),
//         sortOrder: getSortOrder(),
//       });
      
//       // Add search param if present
//       if (searchQuery.trim()) {
//         params.append('search', searchQuery.trim());
//       }
      
//       // Add type filter if not "all"
//       if (userTypeFilter !== "all") {
//         params.append('filter', userTypeFilter);
//       }
      
//       const res = await axios.get(`/api/admin/users?${params.toString()}`, {
//         headers: { Authorization: `Bearer ${token}` },
//       });
      
//       console.log("Fetched users:", res.data);
      
//       // Handle paginated response
//       if (res.data.users) {
//         setUsers(res.data.users);
//         setTotalPages(res.data.totalPages || 1);
//         setTotalUsers(res.data.total || 0);
//       } else {
//         setUsers(res.data || []);
//         setTotalPages(1);
//         setTotalUsers(res.data?.length || 0);
//       }
      
//       setIsLoading(false);
//     } catch (err) {
//       console.error("Error fetching users:", err);
//       setStatus("Error fetching user list.");
//       setIsLoading(false);
//     }
//   };

//   // Function to handle page changes
//   const handlePageChange = (newPage) => {
//     if (newPage > 0 && newPage <= totalPages) {
//       setCurrentPage(newPage);
//     }
//   };

//   // Function to handle search
//   const handleSearch = () => {
//     setCurrentPage(1); // Reset to first page on new search
//     fetchUsers();
//   };

//   // Helper functions for sorting
//   const getSortField = () => {
//     switch (sortBy) {
//       case "name":
//         return "firstName";
//       case "email":
//         return "email";
//       case "recent":
//       default:
//         return "createdAt";
//     }
//   };

//   const getSortOrder = () => {
//     // For "recent" we want newest first (desc), for others asc order
//     return sortBy === "recent" ? "desc" : "asc";
//   };

//   // Fetch admin-created notifications for management with pagination
//   const fetchAdminNotifications = async (currentPage = page) => {
//     setListStatus("Loading notifications...");
//     const token = localStorage.getItem("token");
//     try {
//       console.log("Fetching admin notifications with page", currentPage);
//       const res = await axios.get(
//         `/api/notifications/admin/created?page=${currentPage}&limit=20`,
//         {
//           headers: { Authorization: `Bearer ${token}` },
//         }
//       );
//       console.log("Fetched notifications:", res.data);
//       setAdminNotifications(res.data.notifications);
//       setTotalPages(res.data.totalPages);
//       setPage(res.data.currentPage);
//       setListStatus("");
//     } catch (error) {
//       console.error("Error fetching admin notifications:", error);
//       setListStatus("Error fetching notifications: " + error.message);
//     }
//   };

//   useEffect(() => {
//     fetchAdminNotifications();
//     fetchSubscriptionStats();

//   }, []);

//   const handleUserSelection = (userId, isSelected) => {
//     if (isSelected) {
//       setSelectedUsers((prev) => [...prev, userId]);
//     } else {
//       setSelectedUsers((prev) => prev.filter((id) => id !== userId));
//     }
//   };

//   const handleSend = async () => {
//     setStatus("Sending...");
//     const token = localStorage.getItem("token");
//     try {
//       console.log("Sending notification with payload:", {
//         selectedUsers: filter === "custom" ? selectedUsers : [],
//         filter,
//         channels,
//         messageContent,
//         emailSubject,
//       });
//       await axios.post(
//         "/api/notifications/admin/send",
//         {
//           selectedUsers: filter === "custom" ? selectedUsers : [],
//           filter,
//           channels,
//           messageContent,
//           emailSubject,
//         },
//         {
//           headers: { Authorization: `Bearer ${token}` },
//         }
//       );
//       setStatus("Notifications sent successfully!");
//       // Refresh the admin notifications list after sending
//       fetchAdminNotifications();
//     } catch (error) {
//       console.error("Error sending notifications:", error);
//       setStatus(
//         "Error sending notifications: " +
//           (error.response?.data?.error || error.message)
//       );
//     }
//   };

//   // Toggle (enable/disable) a notification's active status
//   const toggleNotification = async (notificationId, currentStatus) => {
//     const token = localStorage.getItem("token");
//     try {
//       console.log(
//         `Toggling notification ${notificationId} from active=${currentStatus} to active=${!currentStatus}`
//       );
//       await axios.put(
//         `/api/notifications/admin/${notificationId}`,
//         { active: !currentStatus },
//         { headers: { Authorization: `Bearer ${token}` } }
//       );
//       // Update local state
//       setAdminNotifications((prev) =>
//         prev.map((notif) =>
//           notif._id === notificationId ? { ...notif, active: !currentStatus } : notif
//         )
//       );
//     } catch (error) {
//       console.error("Error updating notification status:", error);
//       setListStatus("Error updating notification status: " + error.message);
//     }
//   };

//   // Remove a notification
//   const removeNotification = async (notificationId) => {
//     const token = localStorage.getItem("token");
//     try {
//       console.log(`Removing notification ${notificationId}`);
//       await axios.delete(`/api/notifications/admin/${notificationId}`, {
//         headers: { Authorization: `Bearer ${token}` },
//       });
//       // Update local state: remove the deleted notification
//       setAdminNotifications((prev) =>
//         prev.filter((notif) => notif._id !== notificationId)
//       );
//     } catch (error) {
//       console.error("Error deleting notification:", error);
//       setListStatus("Error deleting notification: " + error.message);
//     }
//   };

//   // Pagination controls
//   const handlePrevPage = () => {
//     if (page > 1) {
//       fetchAdminNotifications(page - 1);
//     }
//   };

//   const handleNextPage = () => {
//     if (page < totalPages) {
//       fetchAdminNotifications(page + 1);
//     }
//   };

//   // Function to handle template selection
//   const handleTemplateChange = (e) => {
//     const templateKey = e.target.value;
//     setSelectedTemplate(templateKey);
//     setMessageContent((prev) => ({
//       ...prev,
//       email: emailTemplates[templateKey].html,
//     }));
//   };

//   // Function to insert a variable at the cursor position in the editor
//   const insertVariable = (variable) => {
//     if (contentEditableRef.current) {
//       // Get the current selection in the editor
//       const selection = window.getSelection();
//       if (selection && selection.rangeCount > 0) {
//         const range = selection.getRangeAt(0);
//         // Create a text node with the variable placeholder
//         const textNode = document.createTextNode(variable);
//         // Insert the text node at the current cursor position
//         range.insertNode(textNode);
//         // Move the cursor after the inserted text node
//         range.setStartAfter(textNode);
//         range.setEndAfter(textNode);
//         selection.removeAllRanges();
//         selection.addRange(range);
//       } else {
//         // If no selection, append the variable at the end
//         contentEditableRef.current.innerHTML += ' ' + variable;
//       }
//       // Update the state with the new HTML content of the editor
//       setMessageContent((prev) => ({
//         ...prev,
//         email: contentEditableRef.current.innerHTML,
//       }));
//       setShowVariableMenu(false);
//     }
//   };
  

//   // Handle change in the contentEditable component
//   const handleContentEditableChange = (evt) => {
//     setMessageContent({
//       ...messageContent,
//       email: evt.target.value
//     });
//   };

//   if (loading) {
//     return (
//       <div className="fixed inset-0 flex flex-col items-center justify-center">
//         <img src='../images/loading.gif'/>
//       </div>
//     );
//   }

//   return (
//     <div>
//       <h2 className="text-2xl font-semibold mb-8">Notification/Newsletter Management</h2>
//       <div className="px-4 py-4 bg-white shadow-lg rounded-2xl">

//         {/*Create New Notification */}
//         <div>
//           <div className="mb-6 pt-2">
//             <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3 rounded-lg shadow-md mb-8">
//               <h3 className="text-md font-semibold text-white">Create New Notification</h3>
//             </div>

//             <div className="p-4"> 
//               {/* User Selection */}
//               <h4 className="text-md font-semibold text-gray-700 mb-4">
//                 Select Recipients
//               </h4>
//               <div className="flex flex-col sm:flex-row sm:space-x-6">
//                 {/* <label className="inline-flex items-center mb-2 sm:mb-0">
//                   <input
//                     type="radio"
//                     value="all"
//                     checked={filter === "all"}
//                     onChange={() => setFilter("all")}
//                     className="form-radio text-blue-600"
//                   />
//                   <span className="ml-2">All Users</span>
//                 </label>
//                 <label className="inline-flex items-center">
//                   <input
//                     type="radio"
//                     value="custom"
//                     checked={filter === "custom"}
//                     onChange={() => setFilter("custom")}
//                     className="form-radio text-blue-600"
//                   />
//                   <span className="ml-2">Custom Selection</span>
//                 </label> */}
//                  <label className="inline-flex items-center mb-2 sm:mb-0">
//                   <input
//                     type="radio"
//                     value="subscribers"
//                     checked={filter === "subscribers"}
//                     onChange={() => setFilter("subscribers")}
//                     className="form-radio text-blue-600"
//                   />
//                   <span className="ml-2">All Subscribers ({subscriptionStats.active})</span>
//                 </label>
//                 <label className="inline-flex items-center mb-2 sm:mb-0">
//                   <input
//                     type="radio"
//                     value="all"
//                     checked={filter === "all"}
//                     onChange={() => setFilter("all")}
//                     className="form-radio text-blue-600"
//                   />
//                   <span className="ml-2">All Users</span>
//                 </label>
//                 <label className="inline-flex items-center">
//                   <input
//                     type="radio"
//                     value="custom"
//                     checked={filter === "custom"}
//                     onChange={() => setFilter("custom")}
//                     className="form-radio text-blue-600"
//                   />
//                   <span className="ml-2">Custom Selection</span>
//                 </label>
//               </div>
           
//               {filter === "custom" && (
//                 <div className="mb-6 mt-4">
//                   <h4 className="text-md font-semibold text-gray-700 mb-2">
//                     Select Individual Users
//                   </h4>
                  
//                   {/* Search and Filter Controls */}
//                   <div className="mb-4 flex flex-col md:flex-row gap-3">
//                     <div className="relative flex-grow">
//                       <input
//                         type="text"
//                         placeholder="Search by name or email..."
//                         className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         value={searchQuery}
//                         onChange={(e) => setSearchQuery(e.target.value)}
//                         onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
//                       />
//                       <button 
//                         className="absolute right-2 top-2 text-gray-400 hover:text-blue-500"
//                         onClick={handleSearch}
//                         style={{transform:'none', boxShadow:'none', border: 'none'}}
//                       >
//                         <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
//                         </svg>
//                       </button>
//                     </div>
                    
//                     {/* <div className="flex-shrink-0">
//                       <select
//                         className="w-full md:w-auto px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         value={userTypeFilter}
//                         onChange={(e) => setUserTypeFilter(e.target.value)}
//                       >
//                         <option value="all">All Users</option>
//                         <option value="subscribers">Subscribers Only</option>
//                         <option value="active">Recently Active</option>
//                       </select>
//                     </div> */}
                    
//                     <div className="flex-shrink-0">
//                       <select
//                         className="w-full md:w-auto pr-8 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         value={sortBy}
//                         onChange={(e) => setSortBy(e.target.value)}
//                       >
//                         <option value="recent">Recently Added</option>
//                         <option value="name">Name (A-Z)</option>
//                         <option value="email">Email (A-Z)</option>
//                       </select>
//                     </div>
//                   </div>
                  
//                   {/* Users Grid with Loading State */}
//                   {isLoading ? (
//                     <div className="flex justify-center items-center h-40">
//                       <div className="spinner-border text-blue-500" role="status">
//                         <span className="sr-only">Loading...</span>
//                         <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                           <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                           <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                         </svg>
//                       </div>
//                     </div>
//                   ) : users.length > 0 ? (
//                     <div className="border p-4 rounded bg-gray-50">
//                       <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 max-h-60 overflow-y-auto">
//                         {users.map((user) => (
//                           <label key={user._id} className="flex items-center space-x-2 px-3 py-2 rounded hover:bg-gray-100 transition bg-white shadow-sm">
//                             <input
//                               type="checkbox"
//                               value={user._id}
//                               onChange={(e) =>
//                                 handleUserSelection(user._id, e.target.checked)
//                               }
//                               checked={selectedUsers.includes(user._id)}
//                               className="form-checkbox text-blue-600 rounded"
//                             />
//                             <div className="text-sm">
//                               <div className="font-medium text-gray-700">{user.email}</div>
//                               {user.firstName && (
//                                 <div className="text-gray-500 text-xs">{user.firstName} {user.lastName}</div>
//                               )}
//                             </div>
//                           </label>
//                         ))}
//                       </div>
                      
//                       {/* Pagination Controls */}
//                       <div className="mt-4 flex flex-col sm:flex-row justify-between items-center">
//                         <div className="text-sm text-gray-500 mb-2 sm:mb-0">
//                           Showing {users.length} of {totalUsers} users
//                         </div>
//                         <div className="flex justify-center space-x-2">
//                           <button
//                             onClick={() => handlePageChange(currentPage - 1)}
//                             disabled={currentPage === 1}
//                             className={`px-3 py-1 rounded ${
//                               currentPage === 1
//                                 ? "bg-gray-200 text-gray-500 cursor-not-allowed"
//                                 : "bg-blue-100 text-blue-700 hover:bg-blue-200"
//                             }`}
//                           >
//                             &laquo; Prev
//                           </button>
//                           <div className="flex items-center px-2">
//                             <span className="text-sm">Page {currentPage} of {totalPages}</span>
//                           </div>
//                           <button
//                             onClick={() => handlePageChange(currentPage + 1)}
//                             disabled={currentPage === totalPages || totalPages === 0}
//                             className={`px-3 py-1 rounded ${
//                               currentPage === totalPages || totalPages === 0
//                                 ? "bg-gray-200 text-gray-500 cursor-not-allowed"
//                                 : "bg-blue-100 text-blue-700 hover:bg-blue-200"
//                             }`}
//                           >
//                             Next &raquo;
//                           </button>
//                         </div>
//                       </div>
//                     </div>
//                   ) : (
//                     <div className="text-center py-10 border rounded bg-gray-50">
//                       <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
//                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
//                       </svg>
//                       <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
//                       <p className="mt-1 text-sm text-gray-500">
//                         Try adjusting your search or filter criteria.
//                       </p>
//                     </div>
//                   )}
                  
//                   {/* Selection Summary */}
//                   <div className="mt-2 flex justify-between text-sm text-gray-500">
//                     <span>{selectedUsers.length} users selected</span>
//                     {selectedUsers.length > 0 && (
//                       <button 
//                         onClick={() => setSelectedUsers([])}
//                         className="text-red-500 hover:text-red-700"
//                       >
//                         Clear selection
//                       </button>
//                     )}
//                   </div>
//                 </div>
//               )}

//               {/* Channel Selection */}
//               <div className="mb-6 mt-8">
//                 <h4 className="text-md font-semibold text-gray-700 mb-4">
//                   Select Notification Channels
//                 </h4>
//                 <div className="flex flex-col sm:flex-row sm:space-x-6 mt-2">
//                   <label className="inline-flex items-center mb-2 sm:mb-0 bg-blue-50 px-4 py-2 rounded-lg hover:bg-blue-100 transition">
//                     <input
//                       type="checkbox"
//                       checked={channels.email}
//                       onChange={(e) =>
//                         setChannels({ ...channels, email: e.target.checked })
//                       }
//                       className="form-checkbox text-blue-600 rounded"
//                     />
//                     <span className="ml-2 flex items-center">
//                       <MailIcon size={16} className="mr-1" /> Email
//                     </span>
//                   </label>
//                   <label className="inline-flex items-center mb-2 sm:mb-0 bg-green-50 px-4 py-2 rounded-lg hover:bg-green-100 transition">
//                     <input
//                       type="checkbox"
//                       checked={channels.sms}
//                       onChange={(e) =>
//                         setChannels({ ...channels, sms: e.target.checked })
//                       }
//                       className="form-checkbox text-green-600 rounded"
//                     />
//                     <span className="ml-2 flex items-center">
//                       <PhoneIcon size={16} className="mr-1" /> SMS
//                     </span>
//                   </label>
//                   <label className="inline-flex items-center bg-purple-50 px-4 py-2 rounded-lg hover:bg-purple-100 transition">
//                     <input
//                       type="checkbox"
//                       checked={channels.inApp}
//                       onChange={(e) =>
//                         setChannels({ ...channels, inApp: e.target.checked })
//                       }
//                       className="form-checkbox text-purple-600 rounded"
//                     />
//                     <span className="ml-2 flex items-center">
//                       <BellIcon size={16} className="mr-1" /> In-App Notification
//                     </span>
//                   </label>
//                 </div>
//               </div>

//               {/* Message Composer */}
//               <div className="mb-6">
//                 {channels.email && (
//                   <div className="mb-4">
//                     <h3 className="text-xl font-semibold text-gray-700 mb-2 flex items-center">
//                       <MailIcon size={20} className="mr-2 text-blue-600" />
//                       Email Content
//                     </h3>
//                     <div className="mb-4">
//                       <label className="block text-gray-700 mb-2">Email Subject</label>
//                       <input
//                         type="text"
//                         value={emailSubject}
//                         onChange={(e) => setEmailSubject(e.target.value)}
//                         placeholder="Enter email subject"
//                         className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                       />
//                     </div>

//                     <div className="mb-4">
//                       <div className="flex justify-between items-center mb-2">
//                         <label className="block text-gray-700">Choose Template</label>
//                       </div>
//                       <select
//                         value={selectedTemplate}
//                         onChange={handleTemplateChange}
//                         className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
//                       >
//                         {Object.keys(emailTemplates).map((key) => (
//                           <option key={key} value={key}>
//                             {emailTemplates[key].name}
//                           </option>
//                         ))}
//                       </select>
//                     </div>

//                     {/* Variable insertion tools */}
//                     <div className="mb-4">
//                       <div className="flex justify-between items-center mb-2">
//                         <label className="block text-gray-700">Insert Variables</label>
//                         <button
//                           type="button"
//                           onClick={() => setShowVariableMenu(!showVariableMenu)}
//                           className="flex items-center text-blue-600 hover:text-blue-800"
//                         >
//                           <TagIcon size={16} className="mr-1" /> 
//                           {showVariableMenu ? "Hide Variables" : "Show Variables"}
//                         </button>
//                       </div>
                      
//                       {showVariableMenu && (
//                         <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 p-3 bg-gray-50 rounded-md mb-4">
//                           {variablesList.map((variable) => (
//                             <button
//                               key={variable.value}
//                               onClick={() => insertVariable(variable.value)}
//                               className="flex items-center px-3 py-2 bg-white rounded border hover:bg-blue-50 transition text-sm"
//                             >
//                               {variable.icon}
//                               <span className="ml-2">{variable.label}</span>
//                             </button>
//                           ))}
//                         </div>
//                       )}
//                     </div>

//                     {/* Editable Email Preview */}
//                     <div className="border rounded-md bg-white">
//                       <div className="mb-2 p-2 bg-gray-100 rounded text-sm text-gray-700 mx-4 mt-4">
//                         <strong>Subject:</strong> {emailSubject}
//                       </div>
//                       <div className="p-4 email-preview">
//                         <div className="border p-4 rounded-md shadow-sm mb-2 bg-gray-50">
//                           <p className="text-sm text-gray-600 mb-2">
//                             <strong>Note:</strong> Click anywhere in the template below to edit. You can add variables using the buttons above.
//                           </p>
//                         </div>
//                         <ContentEditable
//                           innerRef={contentEditableRef}
//                           html={messageContent.email}
//                           disabled={false}
//                           onChange={handleContentEditableChange}
//                           className="min-h-96 outline-none border border-gray-200 p-4 rounded-md focus:border-blue-400 focus:ring-2 focus:ring-blue-200"
//                         />
//                       </div>
//                     </div>
                    
//                     <div className="mt-2 text-sm text-gray-600">
//                       <p>Use variables like <code>{"{{firstName}}"}</code> to personalize your email. Click "Show Variables" to see all options.</p>
//                     </div>
//                   </div>
//                 )}

//                 {channels.sms && (
//                   <div className="mb-4">
//                     <h3 className="text-xl font-semibold text-gray-700 mb-2 flex items-center">
//                       <PhoneIcon size={20} className="mr-2 text-green-600" />
//                       SMS Content
//                     </h3>
//                     <div className="mb-2">
//                       <div className="flex justify-between items-center mb-2">
//                         <label className="block text-gray-700">Message Text (160 characters recommended)</label>
//                         <span className="text-sm text-gray-500">{messageContent.sms.length} characters</span>
//                       </div>
//                       <textarea
//                         rows="3"
//                         className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-green-400"
//                         placeholder="Compose your SMS message here..."
//                         value={messageContent.sms}
//                         onChange={(e) =>
//                           setMessageContent({ ...messageContent, sms: e.target.value })
//                         }
//                       />
//                     </div>
//                     <div className="mt-2 text-sm text-gray-600">
//                       <p>For SMS, you can use variables like <code>{"{firstName}"}</code> too. Keep messages concise for better delivery rates.</p>
//                     </div>
//                   </div>
//                 )}
//                 {channels.inApp && (
//                   <div className="mb-4">
//                     <h3 className="text-xl font-semibold text-gray-700 mb-2 flex items-center">
//                       <BellIcon size={20} className="mr-2 text-purple-600" />
//                       In-App Notification Content
//                     </h3>
//                     <textarea
//                       rows="3"
//                       className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-purple-400"
//                       placeholder="Compose your in-app notification here..."
//                       value={messageContent.inApp}
//                       onChange={(e) =>
//                         setMessageContent({ ...messageContent, inApp: e.target.value })
//                       }
//                     />
//                     <div className="mt-2 text-sm text-gray-600">
//                       <p>In-app notifications should be short and actionable. You can include variables like <code>{"{firstName}"}</code> here too.</p>
//                     </div>
//                   </div>
//                 )}
//               </div>
//             </div>

//             {/* Send Button & Status */}
//             <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-4 mb-10 px-4">
//               <button
//                 onClick={handleSend}
//                 disabled={!channels.email && !channels.sms && !channels.inApp}
//                 className={`px-6 py-3 rounded-lg transition-colors flex items-center ${
//                   (!channels.email && !channels.sms && !channels.inApp)
//                     ? "bg-gray-300 text-gray-500 cursor-not-allowed"
//                     : "bg-green-600 text-white hover:bg-green-700"
//                 }`}
//               >
//                 <SendIcon size={18} className="mr-2" />
//                 Send Notification
//               </button>
//               {status && (
//                 <div className={`text-lg font-medium ${status.includes('Error') ? 'text-red-600' : 'text-green-600'}`}>
//                   {status}
//                 </div>
//               )}
//             </div>

//           </div>
//         </div>

//         {/* --- Section 2: Manage Created Notifications --- */}
//         <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3 rounded-lg shadow-md mb-8">
//           <h3 className="text-md font-semibold text-white">Manage Created Notifications</h3>
//         </div>

//         {listStatus && <div className="mb-4 text-red-600">{listStatus}</div>}
//         {adminNotifications.length === 0 ? (
//           <div className="text-center py-12">
//             <div className="mb-4">
//               <InboxIcon size={64} className="text-gray-300 mx-auto" />
//             </div>
//             <p className="text-gray-500 text-lg">No notifications have been created yet.</p>
//             <p className="text-gray-400 mt-2">Send your first notification using the form above.</p>
//           </div>
//         ) : (
//           <div className="overflow-x-auto -mx-2 lg:mx-0">
//             <table className="min-w-full bg-white shadow-md rounded-lg">
//               <thead className="bg-gray-50">
//                 <tr>
//                   <th className="py-3 px-3 lg:px-4 border-b text-sm font-medium text-gray-700">Message</th>
//                   <th className="py-3 px-3 lg:px-4 border-b text-sm font-medium text-gray-700 hidden lg:table-cell">Type</th>
//                   <th className="py-3 px-3 lg:px-4 border-b text-sm font-medium text-gray-700 hidden lg:table-cell">Created At</th>
//                   <th className="py-3 px-3 lg:px-4 border-b text-sm font-medium text-gray-700 hidden lg:table-cell">Status</th>
//                   <th className="py-3 px-3 lg:px-4 border-b text-sm font-medium text-gray-700">Actions</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {adminNotifications.map((notif) => (
//                   <tr key={notif._id} className="text-center hover:bg-gray-50 border-b">
//                     <td className="py-3 px-3 lg:px-4 text-sm">
//                       <div className="font-medium text-left lg:text-center">{notif.message}</div>
//                       <div className="lg:hidden mt-2 text-sm text-left text-gray-600">
//                         <div className="mb-1"><span className="font-medium">{notif.type}</span></div>
//                         <div className="mb-1">
//                           <span className={`inline-block px-2 py-1 rounded-full text-sm ${
//                             notif.active 
//                               ? "bg-green-100 text-green-800" 
//                               : "bg-gray-100 text-gray-800"
//                           }`}>
//                             {notif.active ? "Active" : "Disabled"}
//                           </span>
//                         </div>
//                         <div className="text-sm">{new Date(notif.createdAt).toLocaleDateString()}</div>
//                       </div>
//                     </td>
//                     <td className="py-3 px-3 lg:px-4 text-sm hidden lg:table-cell">{notif.type}</td>
//                     <td className="py-3 px-3 lg:px-4 text-sm hidden lg:table-cell">
//                       {new Date(notif.createdAt).toLocaleString()}
//                     </td>
//                     <td className="py-3 px-3 lg:px-4 text-sm hidden lg:table-cell">
//                       <span className={`inline-block px-2 py-1 rounded-full text-sm ${
//                         notif.active 
//                           ? "bg-green-100 text-green-800" 
//                           : "bg-gray-100 text-gray-800"
//                       }`}>
//                         {notif.active ? "Active" : "Disabled"}
//                       </span>
//                     </td>
//                     <td className="py-3 px-3 lg:px-4">
//                       <div className="flex flex-row justify-center space-x-4">
//                         <button
//                           onClick={() => toggleNotification(notif._id, notif.active)}
//                           className={`p-2 rounded-full ${
//                             notif.active
//                               ? "bg-red-100 text-red-500 hover:bg-red-200"
//                               : "bg-green-100 text-green-500 hover:bg-green-200"
//                           }`}
//                           title={notif.active ? "Disable" : "Enable"}
//                         >
//                           {notif.active ? <BanIcon size={20} /> : <CheckCircleIcon size={20} />}
//                         </button>
//                         <button
//                           onClick={() => removeNotification(notif._id)}
//                           className="p-2 rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200"
//                           title="Remove"
//                         >
//                           <TrashIcon size={20} />
//                         </button>
//                       </div>
//                     </td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           </div>
//         )}

//         {/* Pagination Controls */}
//         {adminNotifications.length > 0 && (
//           <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-3">
//             <button
//               onClick={handlePrevPage}
//               disabled={page === 1}
//               className="w-full sm:w-auto px-5 py-2.5 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 disabled:opacity-50 flex items-center justify-center text-base"
//             >
//               <span className="mr-1">←</span> Previous
//             </button>
//             <span className="text-gray-700 text-base">
//               Page {page} of {totalPages}
//             </span>
//             <button
//               onClick={handleNextPage}
//               disabled={page === totalPages}
//               className="w-full sm:w-auto px-5 py-2.5 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 disabled:opacity-50 flex items-center justify-center text-base"
//             >
//               Next <span className="ml-1">→</span>
//             </button>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default NotificationsManagement;





